import React from "react";
import { Box, Card, Chip, Sheet, Divider, CircularProgress } from "@mui/joy";
import { AddIcon } from "../../../styles/StyledMUI";
import IconButtonCTA from "../../common/IconButtonCTA";
import { useLeave } from "../../../contexts/api";
import { useHRData } from "../../../contexts/app/hrData";
import { useAuth } from "../../../contexts/api";
import type { LeaveRequest, LeaveRequestsQuery, LeaveType } from "../../../api/leave/Leave.typed";
import { LeaveStatus } from "../../../api/leave/Leave.typed";
import Pagination from "../../common/Pagination";
import DarkTypography from "../../common/DarkTypography";
import { getStatusChip, getDefaultDateRange } from "../../../utils/leaveHelpers";
import {
  DEFAULT_PAGINATION_LIMIT,
  extractDataFromResponse,
  extractPaginationMetadata,
} from "../../../utils/paginationHelpers";
import { userDisplayName } from "../../../utils/userHelper";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import useAppSettings from "../../../contexts/app/appSettings";
import { usePermissions, Permission } from "../../../contexts/app/permissions";
import type { LeaveMotifVisibilityOpts } from "../../../utils/planningLeaveHelpers";
import { colorTokens, hrCardSx, hrSheetSx, textTokens } from "../../../styles/globalStyles";
import CommentsModal from "../../common/CommentsModal";
import type { MultiComment } from "../../../api/zekay/comments";
import LeaveRequestListDesktop from "./LeaveRequestListDesktop";
import LeaveRequestListMobile from "./LeaveRequestListMobile";

function leaveMultiCommentsToModal(mc: LeaveRequest["multi_comments"]): MultiComment[] {
  if (mc == null || !Array.isArray(mc)) return [];
  return mc.map((c) => ({ User: c.user, Comment: c.comment, Date: c.date }));
}

interface LeaveRequestsListProps {
  showMyRequestsOnly: boolean;
  onNewRequest?: () => void;
  newRequestDisabled?: boolean;
  headerExtraActions?: React.ReactNode;
}

export default function LeaveRequestsList({
  showMyRequestsOnly,
  onNewRequest,
  newRequestDisabled = false,
  headerExtraActions,
}: LeaveRequestsListProps) {
  const { getLeaveRequests, deleteLeaveRequest, updateLeaveRequest } = useLeave();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const hasPlanningMeView = hasPermission(Permission.PLANNING_ME_VIEW);
  const { leaveTypes } = useHRData();
  const { config } = useAppSettings();
  const modal = useConfirmationModal();

  const [requests, setRequests] = React.useState<LeaveRequest[]>([]);
  const [loading, setLoading] = React.useState(false);
  const typesMap = React.useMemo(() => {
    const map: Record<number, LeaveType> = {};
    for (const t of leaveTypes) map[t.ID] = t;
    return map;
  }, [leaveTypes]);

  const leaveMotifVisibilityOpts = React.useMemo<LeaveMotifVisibilityOpts>(
    () => ({
      viewerUserId: user?.id ?? null,
      viewerHasPlanningMeView: hasPlanningMeView,
    }),
    [user?.id, hasPlanningMeView],
  );

  const [openCommentsRequestId, setOpenCommentsRequestId] = React.useState<number | null>(null);
  const [openComments, setOpenComments] = React.useState<MultiComment[] | undefined>(undefined);

  const [page, setPage] = React.useState(1);
  const [totalRequests, setTotalRequests] = React.useState(0);
  const [limit, setLimit] = React.useState(DEFAULT_PAGINATION_LIMIT);

  const loadRequests = React.useCallback(async () => {
    setLoading(true);
    const defaultDates = getDefaultDateRange(config?.TimeZone);
    const query = {
      Page: page,
      Limit: limit,
      StartDate: defaultDates.startDate,
      EndDate: defaultDates.endDate,
      ...(showMyRequestsOnly && user ? { UserID: user.id } : {}),
    } as LeaveRequestsQuery;

    const response = await getLeaveRequests(query);
    const requestsData = extractDataFromResponse(response) as LeaveRequest[];
    setRequests(requestsData);

    const metadata = extractPaginationMetadata(response);
    if (metadata) {
      setTotalRequests(metadata.total);
    } else {
      setTotalRequests(requestsData.length);
    }
    setLoading(false);
  }, [getLeaveRequests, showMyRequestsOnly, user, page, limit, config?.TimeZone]);

  React.useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  React.useEffect(() => {
    if (openCommentsRequestId == null) {
      setOpenComments(undefined);
      return;
    }
    const req = requests.find((r) => r.id === openCommentsRequestId);
    setOpenComments(leaveMultiCommentsToModal(req?.multi_comments));
  }, [openCommentsRequestId, requests]);

  const handleCancelOrDelete = React.useCallback(
    async (request: LeaveRequest) => {
      if (request.user_id !== user?.id) return;
      if (request.status === LeaveStatus.PENDING) {
        const ok = await modal.showConfirmation(
          "Supprimer la demande ?",
          "Cette action est irréversible.",
        );
        if (!ok) return;
        await deleteLeaveRequest(request.id);
        loadRequests();
        return;
      }
      if (request.status === LeaveStatus.APPROVED) {
        const leaveType = typesMap[request.type_id];
        const isAutoApproval = leaveType && !leaveType.RequiresValidation;
        const newStatus = isAutoApproval
          ? LeaveStatus.CANCELLED
          : LeaveStatus.CANCELLATION_REQUESTED;
        const ok = await modal.showConfirmation(
          isAutoApproval ? "Annuler la demande ?" : "Demander l'annulation ?",
          isAutoApproval
            ? "La demande sera annulée."
            : "La demande passera en Annulation demandée.",
        );
        if (!ok) return;
        await updateLeaveRequest(request.id, { Status: newStatus });
        loadRequests();
      }
    },
    [deleteLeaveRequest, loadRequests, modal, typesMap, updateLeaveRequest, user?.id],
  );

  const canCancelOrDeleteLocal = React.useCallback(
    (request: LeaveRequest) => {
      if (request.user_id !== user?.id) return false;
      if (request.status === LeaveStatus.CANCELLATION_REQUESTED) return false;
      return request.status === LeaveStatus.PENDING || request.status === LeaveStatus.APPROVED;
    },
    [user?.id],
  );

  const renderStatusChip = (status: number) => {
    const statusInfo = getStatusChip(status);
    if (!statusInfo) return null;

    return (
      <Chip size="sm" color={statusInfo.color} variant="soft">
        {statusInfo.name}
      </Chip>
    );
  };

  const handleCommentsSave = React.useCallback(
    async (updated: MultiComment[] | undefined) => {
      if (openCommentsRequestId == null) return;
      const req = requests.find((r) => r.id === openCommentsRequestId);
      await updateLeaveRequest(openCommentsRequestId, {
        Status: req?.status ?? 1,
        MultiComments: updated || [],
      });
      await loadRequests();
    },
    [loadRequests, openCommentsRequestId, requests, updateLeaveRequest],
  );

  const handleItemsPerPageChange = React.useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {onNewRequest && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            flexShrink: 0,
          }}
        >
          <DarkTypography level="title-md" colorVariant="accent">
            Mes demandes
          </DarkTypography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {headerExtraActions}
            <IconButtonCTA
              ctaVariant="accent"
              aria-label="Nouvelle demande d'absence"
              onClick={onNewRequest}
              disabled={newRequestDisabled}
              size="sm"
              sx={{
                "&:disabled": {
                  backgroundColor: colorTokens.bg.surfaceMuted,
                  color: textTokens.color.dimmed,
                },
              }}
            >
              <AddIcon />
            </IconButtonCTA>
          </Box>
        </Box>
      )}
      {requests.length === 0 ? (
        <Card sx={{ p: { xs: 2, sm: 3 }, textAlign: "center", ...hrCardSx }}>
          <DarkTypography level="body-md">Aucune demande trouvée</DarkTypography>
        </Card>
      ) : (
        <>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Sheet sx={{ ...hrSheetSx, p: { xs: 1, md: 0 } }}>
              <LeaveRequestListDesktop
                requests={requests}
                typesMap={typesMap}
                canCancelOrDeleteLocal={canCancelOrDeleteLocal}
                handleCancelOrDelete={handleCancelOrDelete}
                setOpenCommentsRequestId={setOpenCommentsRequestId}
                renderStatusChip={renderStatusChip}
                leaveMotifVisibilityOpts={leaveMotifVisibilityOpts}
              />
              <LeaveRequestListMobile
                requests={requests}
                typesMap={typesMap}
                canCancelOrDeleteLocal={canCancelOrDeleteLocal}
                handleCancelOrDelete={handleCancelOrDelete}
                setOpenCommentsRequestId={setOpenCommentsRequestId}
                renderStatusChip={renderStatusChip}
                leaveMotifVisibilityOpts={leaveMotifVisibilityOpts}
              />
            </Sheet>
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            <Divider sx={{ my: 1 }} />
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(totalRequests / limit)}
              totalItems={totalRequests}
              itemsPerPage={limit}
              onPageChange={setPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </Box>

          <CommentsModal
            open={openCommentsRequestId != null}
            title="Commentaires"
            currentUserName={user ? userDisplayName(user) : ""}
            multiComments={openComments}
            setMultiComments={setOpenComments}
            onClose={() => setOpenCommentsRequestId(null)}
            onSave={handleCommentsSave}
          />
        </>
      )}
    </Box>
  );
}
